import { Extension, Node, mergeAttributes } from "@tiptap/core"

interface AttachmentChipAttrs {
  id: string
  name: string
}

/**
 * Rendered as a plain `<span data-attachment-id>` rather than a `<a href>` —
 * an attachment's URL is often a short-lived signed S3 link, so the node
 * stores only the durable attachment id and lets the viewer resolve today's
 * URL against the entity's live attachment list at render time.
 */
const AttachmentChip = Node.create({
  name: "attachmentChip",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-attachment-id"),
        renderHTML: (attrs: AttachmentChipAttrs) => ({
          "data-attachment-id": attrs.id
        })
      },
      name: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-attachment-name"),
        renderHTML: (attrs: AttachmentChipAttrs) => ({
          "data-attachment-name": attrs.name
        })
      }
    }
  },

  parseHTML() {
    return [{ tag: "span[data-attachment-id]" }]
  },

  renderHTML({ HTMLAttributes, node }) {
    // No `class` — the sanitizer strips it from saved HTML (see
    // rich-text-html.ts), so the chip's look is keyed off
    // `[data-attachment-id]` in CSS instead, which survives sanitizing.
    return [
      "span",
      mergeAttributes(HTMLAttributes, { contenteditable: "false" }),
      `📎 ${node.attrs.name}`
    ]
  }
})

/** Tab/Shift-Tab nest and un-nest a list item — the only "indent" a compact
 * note editor offers, and StarterKit's ListItem binds neither by default. */
const ListIndentKeymap = Extension.create({
  name: "listIndentKeymap",
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.sinkListItem("listItem"),
      "Shift-Tab": () => this.editor.commands.liftListItem("listItem")
    }
  }
})

export { type AttachmentChipAttrs, AttachmentChip, ListIndentKeymap }
